"use client";

import { ContactDialogType, UserTypes } from "@/types/types";
import { AgencyProfile } from "../types";
import styles from "./profile-card.module.css";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import { useDeviceType } from "@/hooks/use-device-type";
import { Link } from "@/i18n/navigation";
import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import {
  SOCIAL_CONFIG,
  SocialPlatform,
} from "../../../profiles/[usersub]/types";
import { motion } from "framer-motion";
import {
  formatWebsite,
  googleSearchMapUrl,
  isValidPhone,
  normalizePhone,
} from "@/lib/profiles-helpers";
import ContactDialog from "@/components/contact-agent-info/seller-contact-dialog";
import { useScrollLock } from "@/hooks/use-scroll-lock";

export default function ProfileCard({
  agencyProfile,
}: {
  agencyProfile: AgencyProfile;
}) {
  const locale = useLocale();
  const displayNames = new Intl.DisplayNames([locale], { type: "region" });
  const [showPhone, setShowPhone] = useState(false);
  const deviceType = useDeviceType();
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [fullHeight, setFullHeight] = useState<number | null>(null);
  const [isClamped, setIsClamped] = useState(true);
  const contentRef = useRef<HTMLParagraphElement | null>(null);
  const validSocials = Object.entries(agencyProfile.socials ?? {}).filter(
    ([key, url]) => {
      if (!url) return false;
      return Boolean(SOCIAL_CONFIG[key as SocialPlatform]);
    },
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const t = useTranslations("AgencyProfiles.infoCard");

  useScrollLock(isDialogOpen);

  const measureContent = () => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    // Remember current clamp state
    const wasClamped = el.classList.contains(styles.clamped);

    // Remove clamp to measure real height
    el.classList.remove(styles.clamped);
    const height = el.scrollHeight;

    setFullHeight(height);
    setCanExpand(height > 120);

    // Restore clamp ONLY if it was there before
    if (wasClamped) {
      el.classList.add(styles.clamped);
    }
  };

  useEffect(() => {
    measureContent();
  }, [agencyProfile.description]);

  useEffect(() => {
    window.addEventListener("resize", measureContent);
    return () => window.removeEventListener("resize", measureContent);
  }, []);

  const maskedPhone = (phone: string | undefined) => {
    if (!phone) return "";
    return `${phone.slice(0, 4)}...`;
  };

  const scrollRefMarkets = useRef<HTMLUListElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const hasMoved = useRef(false);

  useEffect(() => {
    const slider = scrollRefMarkets.current;
    if (!slider) return;

    const handlePointerDown = (e: PointerEvent) => {
      isDragging.current = true;
      startX.current = e.pageX;
      scrollLeft.current = slider.scrollLeft;
      hasMoved.current = false;
      slider.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDragging.current) return;
      const x = e.pageX;
      const walk = x - startX.current;
      if (Math.abs(walk) > 1) hasMoved.current = true;
      slider.scrollLeft = scrollLeft.current - walk;
    };

    const handlePointerUp = (e: PointerEvent) => {
      isDragging.current = false;
      slider.releasePointerCapture(e.pointerId);
    };

    const handleClickCapture = (e: MouseEvent) => {
      if (hasMoved.current) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    slider.addEventListener("pointerdown", handlePointerDown);
    slider.addEventListener("pointermove", handlePointerMove);
    slider.addEventListener("pointerup", handlePointerUp);
    slider.addEventListener("click", handleClickCapture, true); // capture phase

    return () => {
      slider.removeEventListener("pointerdown", handlePointerDown);
      slider.removeEventListener("pointermove", handlePointerMove);
      slider.removeEventListener("pointerup", handlePointerUp);
      slider.removeEventListener("click", handleClickCapture, true);
    };
  }, []);

  const scrollRefSpecialization = useRef<HTMLUListElement>(null);
  const isDraggingSpecialization = useRef(false);
  const startXSpecialization = useRef(0);
  const scrollLeftSpecialization = useRef(0);
  const hasMovedSpecialization = useRef(false);

  useEffect(() => {
    const slider = scrollRefSpecialization.current;
    if (!slider) return;

    const handlePointerDown = (e: PointerEvent) => {
      isDraggingSpecialization.current = true;
      startXSpecialization.current = e.pageX;
      scrollLeftSpecialization.current = slider.scrollLeft;
      hasMovedSpecialization.current = false;
      slider.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDraggingSpecialization.current) return;
      const x = e.pageX;
      const walk = x - startXSpecialization.current;
      if (Math.abs(walk) > 1) hasMovedSpecialization.current = true;
      slider.scrollLeft = scrollLeftSpecialization.current - walk;
    };

    const handlePointerUp = (e: PointerEvent) => {
      isDraggingSpecialization.current = false;
      slider.releasePointerCapture(e.pointerId);
    };

    const handleClickCapture = (e: MouseEvent) => {
      if (hasMovedSpecialization.current) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    slider.addEventListener("pointerdown", handlePointerDown);
    slider.addEventListener("pointermove", handlePointerMove);
    slider.addEventListener("pointerup", handlePointerUp);
    slider.addEventListener("click", handleClickCapture, true); // capture phase

    return () => {
      slider.removeEventListener("pointerdown", handlePointerDown);
      slider.removeEventListener("pointermove", handlePointerMove);
      slider.removeEventListener("pointerup", handlePointerUp);
      slider.removeEventListener("click", handleClickCapture, true);
    };
  }, []);

  return (
    <div className={`${styles.background} ${styles.agency}`}>
      <div className={styles.main}>
        <div className={`${styles.profileCard} ${styles.avatarCard}`}>
          <div className={styles.avatarHeader}>
            <Image
              className={styles.creatorAvatar}
              src={agencyProfile.logo_url}
              width={256}
              height={256}
              alt={agencyProfile.name}
            />
            <div className={styles.nameWrapper}>
              <h2 className={`${styles.userName} `}>{agencyProfile.name}</h2>
            </div>
            <span className={`${styles.badge} ${styles.agency}`}>
              {t("agency")}
            </span>
            <div className={styles.stats}>
              {agencyProfile.founded_year > 0 && (
                <div className={styles.statCol}>
                  <p className={styles.stat}>{agencyProfile.founded_year}</p>
                  <p className={styles.label}>{t("foundedYear")}</p>
                </div>
              )}
              <div className={styles.statCol}>
                <p className={styles.stat}>{agencyProfile.total_listings}</p>
                <p className={styles.label}>{t("activeListings")}</p>
              </div>
            </div>
          </div>
          <button
            className={`${styles.contactButton} ${styles.agency}`}
            onClick={(e) => {
              e.preventDefault();
              setIsDialogOpen(true);
            }}
          >
            {t("contact")}
          </button>
        </div>

        <div className={`${styles.profileCard} ${styles.detailsCard}`}>
          <div className={styles.header}>
            <h3 className={styles.title}>{t("profileDetails")}</h3>

            <div className={styles.infoGrid}>
              {agencyProfile.team_size > 0 && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{t("teamSize")}</span>
                  <span className={styles.infoValue}>
                    {agencyProfile.team_size} {t("people")}
                  </span>
                </div>
              )}
              {agencyProfile.address && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{t("address")}</span>
                  <span className={styles.infoValue}>
                    <a
                      href={googleSearchMapUrl(agencyProfile.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.addressLink}
                      // aria-label={t("openAddressInMapsAria", {
                      //   address: agencyProfile.address,
                      // })}
                    >
                      {agencyProfile.address}
                    </a>
                  </span>
                </div>
              )}
              {agencyProfile.phone_number && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{t("phoneNumber")}</span>
                  {isValidPhone(agencyProfile.phone_number) ? (
                    <span className={styles.infoValue}>
                      {agencyProfile.phone_number && showPhone ? (
                        <a
                          href={`tel:${agencyProfile.phone_number}`}
                          className={styles.phoneLink}
                          // aria-label={t("callPhoneAria", {
                          //   phone: agencyProfile.phone_number,
                          // })}
                        >
                          {agencyProfile.phone_number}
                        </a>
                      ) : (
                        <>
                          <span className={styles.phoneMasked}>
                            {maskedPhone(agencyProfile.phone_number)}
                          </span>
                          <button
                            type="button"
                            className={styles.showPhoneButton}
                            onClick={() => setShowPhone(true)}
                            //   aria-label={t("showPhoneAria", {
                            //     name: agencyProfile.name,
                            //   })}
                          >
                            {t("showNumber")}
                          </button>
                        </>
                      )}
                    </span>
                  ) : (
                    <span className={styles.infoValue}>
                      {agencyProfile.phone_number}
                    </span>
                  )}
                </div>
              )}
              {agencyProfile.website && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{t("website")}</span>
                  <span className={styles.infoValue}>
                    <a
                      href={formatWebsite(agencyProfile.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.websiteLink}
                      // aria-label={t("visitWebsiteAria", { name: agencyProfile.name })}
                    >
                      {agencyProfile.website}
                    </a>
                  </span>
                </div>
              )}

              {agencyProfile.markets && agencyProfile.markets.length > 0 && (
                <div className={`${styles.infoItemFull} ${styles.markets}`}>
                  <span className={styles.infoLabel}>{t("activeIn")}</span>
                  <ul ref={scrollRefMarkets} className={styles.chipsList}>
                    {agencyProfile.markets.map((market) => (
                      <li key={market.slug} className={styles.chip}>
                        <span>{market.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {agencyProfile.specs &&
                Object.values(agencyProfile.specs).some(
                  (isActive) => isActive,
                ) && (
                  <div
                    className={`${styles.infoItemFull} ${styles.specializations}`}
                  >
                    <span className={styles.infoLabel}>
                      {t("specializedIn")}
                    </span>
                    <ul
                      ref={scrollRefSpecialization}
                      className={styles.chipsList}
                    >
                      {(
                        Object.entries(agencyProfile.specs) as [
                          string,
                          boolean,
                        ][]
                      ).map(([key, isActive]) => {
                        // Only render if the specialization is true
                        if (!isActive) return null;

                        return (
                          <li key={key} className={styles.chip}>
                            {/* Optional: Use t(key) if you have translations like "off_plan": "Off Plan" */}
                            <span>{key.replace(/_/g, " ")}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
            </div>
          </div>
          {(validSocials.length > 0 ||
            (agencyProfile.has_whatsapp_active &&
              isValidPhone(agencyProfile.phone_number))) && (
            <div className={styles.socialCard}>
              <span className={styles.socialLabel}>{t("socials")}</span>
              <div className={styles.socialIcons}>
                {agencyProfile.has_whatsapp_active &&
                  isValidPhone(agencyProfile.phone_number) && (
                    <a
                      href={`https://wa.me/${normalizePhone(
                        agencyProfile.phone_number,
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.socialLink}
                      aria-label="WhatsApp"
                    >
                      <img
                        src="/socials/whatsapp.svg"
                        alt="WhatsApp"
                        className={styles.socialIcon}
                      />
                    </a>
                  )}
                {validSocials.map(([key, url]) => {
                  const config = SOCIAL_CONFIG[key as SocialPlatform];
                  if (!config || !url) return null;

                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.socialLink}
                      aria-label={config.label}
                    >
                      <img
                        src={config.icon}
                        alt={config.label}
                        className={styles.socialIcon}
                      />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {((agencyProfile.agents && agencyProfile.agents.length > 0) ||
        agencyProfile.description) && (
        <div className={styles.more}>
          {agencyProfile.description && (
            <div className={`${styles.profileCard} ${styles.aboutCard}`}>
              <h2 className={styles.aboutTitle}>{t("aboutAgency")}</h2>
              <motion.div
                initial={false}
                animate={{
                  maxHeight: expanded && fullHeight ? fullHeight : 120,
                }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                onAnimationComplete={() => {
                  if (!expanded) {
                    setIsClamped(true);
                  }
                }}
                className={styles.descriptionWrapper}
              >
                <p
                  ref={contentRef}
                  className={`${styles.description} ${
                    isClamped ? styles.clamped : ""
                  }`}
                >
                  {agencyProfile.description}
                </p>
              </motion.div>

              {canExpand && (
                <button
                  className={styles.toggleButton}
                  onClick={() => {
                    if (!expanded) {
                      setIsClamped(false);
                    }
                    setExpanded((v) => !v);
                  }}
                >
                  {expanded ? t("seeLess") : t("seeMore")}
                </button>
              )}
            </div>
          )}
          {agencyProfile.agents && agencyProfile.agents.length > 0 && (
            <div className={`${styles.profileCard} ${styles.sideInfoCard}`}>
              <div className={`${styles.profileCard} ${styles.agentsCard}`}>
                <span className={styles.agentsLabel}>{t("agents")}</span>
                <ul className={styles.agentsList}>
                  {agencyProfile.agents.map((agent, _) => {
                    return (
                      <li key={agent.sub} className={styles.agentCard}>
                        <Link
                          className={`${styles.agentLink}`}
                          href={makeLocalizedHrefNoLocale(locale, `profiles`, [
                            agent.sub,
                          ])}
                          aria-label={`Go to ${agent.complete_name} profile`}
                        >
                          <div className={styles.agentDetails}>
                            <Image
                              className={styles.agentAvatar}
                              src={agent.photo_url}
                              width={48}
                              height={48}
                              alt={agent.complete_name}
                            />
                            <div className={styles.agentInfo}>
                              <span className={styles.agentName}>
                                {agent.complete_name}
                              </span>
                              <span className={styles.agentRole}>
                                {t(agent.role)}
                              </span>
                            </div>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
      {isDialogOpen && (
        <ContactDialog
          type={ContactDialogType.GENERAL}
          userSub={agencyProfile.agency_id}
          userType={UserTypes.AGENCY}
          photoUrl={agencyProfile.logo_url}
          userName={agencyProfile.name}
          listingId={""}
          closeCallback={() => {
            setIsDialogOpen(false);
          }}
          phoneNumber={agencyProfile.phone_number}
          hasWhatsAppActive={agencyProfile.has_whatsapp_active}
          dialogRef={dialogRef}
        />
      )}
    </div>
  );
}

export function ProfileCardLoading() {
  return (
    <div className={`${styles.background} ${styles.agency}`}>
      <div className={styles.main}>
        {/* AVATAR CARD */}
        <div className={`${styles.profileCard} ${styles.avatarCard}`}>
          <div className={styles.avatarHeader}>
            <div
              className={`${styles.creatorAvatar} ${styles.skeleton}`}
              style={{ width: 256, height: 256, borderRadius: 500 }}
            />

            <div className={styles.nameWrapper}>
              <h2 className={`${styles.userName} ${styles.skeleton}`}>
                &nbsp;
              </h2>
            </div>

            <span
              className={`${styles.badge} ${styles.agency} ${styles.skeleton}`}
            >
              &nbsp;
            </span>

            <div className={styles.stats}>
              <div className={styles.statCol}>
                <p className={`${styles.stat} ${styles.skeleton}`}>&nbsp;</p>
                <p className={`${styles.label} ${styles.skeleton}`}>&nbsp;</p>
              </div>
              <div className={styles.statCol}>
                <p className={`${styles.stat} ${styles.skeleton}`}>&nbsp;</p>
                <p className={`${styles.label} ${styles.skeleton}`}>&nbsp;</p>
              </div>
            </div>
          </div>

          <div
            className={`${styles.contactButton} ${styles.agency} ${styles.skeleton}`}
            style={{ height: 44 }}
          />
        </div>

        {/* DETAILS CARD */}
        <div className={`${styles.profileCard} ${styles.detailsCard}`}>
          <div className={styles.header}>
            <h3 className={`${styles.title} ${styles.skeleton}`}>&nbsp;</h3>

            <div className={styles.infoGrid}>
              {/* ROW 1 */}
              <div className={styles.infoItem}>
                <span className={`${styles.infoLabel} ${styles.skeleton}`}>
                  &nbsp;
                </span>
                <span
                  className={`${styles.infoValue} ${styles.skeleton}`}
                  style={{ width: "60%" }}
                >
                  &nbsp;
                </span>
              </div>

              <div className={styles.infoItem}>
                <span className={`${styles.infoLabel} ${styles.skeleton}`}>
                  &nbsp;
                </span>
                <span
                  className={`${styles.infoValue} ${styles.skeleton}`}
                  style={{ width: "85%" }}
                >
                  &nbsp;
                </span>
              </div>

              {/* ROW 2 */}
              <div className={styles.infoItem}>
                <span className={`${styles.infoLabel} ${styles.skeleton}`}>
                  &nbsp;
                </span>
                <span
                  className={`${styles.infoValue} ${styles.skeleton}`}
                  style={{ width: "70%" }}
                >
                  &nbsp;
                </span>
              </div>

              <div className={styles.infoItem}>
                <span className={`${styles.infoLabel} ${styles.skeleton}`}>
                  &nbsp;
                </span>
                <span
                  className={`${styles.infoValue} ${styles.skeleton}`}
                  style={{ width: "80%" }}
                >
                  &nbsp;
                </span>
              </div>

              {/* ACTIVE IN – FULL WIDTH */}
              <div className={`${styles.infoItemFull} ${styles.markets}`}>
                <span className={`${styles.infoLabel} ${styles.skeleton}`}>
                  &nbsp;
                </span>
                <ul className={styles.chipsList}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <li
                      key={i}
                      className={`${styles.chip} ${styles.skeleton}`}
                      style={{ width: 130 }}
                    >
                      &nbsp;
                    </li>
                  ))}
                </ul>
              </div>

              {/* SPECIALIZED IN – FULL WIDTH */}
              <div
                className={`${styles.infoItemFull} ${styles.specializations}`}
              >
                <span className={`${styles.infoLabel} ${styles.skeleton}`}>
                  &nbsp;
                </span>
                <ul className={styles.chipsList}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <li
                      key={i}
                      className={`${styles.chip} ${styles.skeleton}`}
                      style={{ width: 150 }}
                    >
                      &nbsp;
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* SOCIALS */}
          <div className={styles.socialCard}>
            <span className={`${styles.socialLabel} ${styles.skeleton}`}>
              &nbsp;
            </span>
            <div className={styles.socialIcons}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className={`${styles.socialIcon} ${styles.skeleton}`}
                  style={{ width: 36, height: 36 }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM SECTION */}
      <div className={styles.more}>
        {/* ABOUT */}
        <div className={`${styles.profileCard} ${styles.aboutCard}`}>
          <h2 className={`${styles.aboutTitle} ${styles.skeleton}`}>&nbsp;</h2>
          <div
            className={`${styles.descriptionWrapper} ${styles.skeleton}`}
            style={{ height: 120 }}
          />
        </div>

        {/* AGENTS */}
        <div className={`${styles.profileCard} ${styles.sideInfoCard}`}>
          <div className={`${styles.profileCard} ${styles.agentsCard}`}>
            <span className={`${styles.agentsLabel} ${styles.skeleton}`}>
              &nbsp;
            </span>
            <ul className={styles.agentsList}>
              {Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className={styles.agentCard} style={{ padding: 8 }}>
                  <div className={styles.agentDetails}>
                    <div
                      className={`${styles.agentAvatar} ${styles.skeleton}`}
                      style={{ width: 48, height: 48, borderRadius: 100 }}
                    />
                    <div className={styles.agentInfo}>
                      <span
                        className={`${styles.agentName} ${styles.skeleton}`}
                        style={{ width: 120 }}
                      >
                        &nbsp;
                      </span>
                      <span
                        className={`${styles.agentRole} ${styles.skeleton}`}
                        style={{ width: 90 }}
                      >
                        &nbsp;
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
